import { useEffect, useRef, useState } from "react";
import { MdClose, MdMoreHoriz, MdVolumeOff, MdVolumeUp } from "react-icons/md";
import DeleteStory from "./DeleteStory";
import { getMediaUrl } from "../../utils/helpers";

export default function StoryViewer({ stories = [], startIndex = 0, onClose, onDeleteStory, showMenuOption }) {
    const [currentIndex, setCurrentIndex] = useState(startIndex);
    const [progress, setProgress] = useState(0);
    const [muted, setMuted] = useState(false);
    const [showPopup, setShowPopup] = useState(false);

    const videoRef = useRef(null);
    const currentStory = stories[currentIndex];

    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === "Escape") onClose();
        };

        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [onClose]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        setProgress(0);
        video.currentTime = 0;

        const playPromise = video.play();
        if (playPromise !== undefined) {
            playPromise.catch(() => {
                // AbortError is expected when switching/unmounting
            });
        }

        return () => {
            video.pause();
        };
    }, [currentIndex]);

    /* ⏸️ PAUSE WHEN TAB IS HIDDEN */
    useEffect(() => {
        const handleVisibility = () => {
            const video = videoRef.current;
            if (!video) return;

            if (document.hidden) {
                video.pause();
            } else {
                const playPromise = video.play();
                if (playPromise !== undefined) {
                    playPromise.catch(() => { });
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibility);
        return () =>
            document.removeEventListener("visibilitychange", handleVisibility);
    }, []);

    /* 📊 PROGRESS BAR */
    const handleTimeUpdate = () => {
        const video = videoRef.current;
        if (!video || !video.duration) return;

        setProgress((video.currentTime / video.duration) * 100);
    };

    /* ⏭️ NEXT STORY */
    const handleEnded = () => {
        if (currentIndex < stories.length - 1) {
            setCurrentIndex(i => i + 1);
        } else {
            onClose();
        }
    };

    /* ⏭️ NEXT STORY (manual) */
    const handleNext = () => {
        if (currentIndex < stories.length - 1) {
            setCurrentIndex(i => i + 1);
        } else {
            onClose();
        }
    };

    /* ⏮️ PREVIOUS STORY */
    const handlePrevious = () => {
        if (currentIndex > 0) {
            setCurrentIndex(i => i - 1);
        }
    };

    if (!currentStory) return null;

    const OnDeleteStory = (storyId) => {
        setShowPopup(false);
        onDeleteStory?.(storyId);
    }

    return (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">

            {/* VIDEO WRAPPER */}
            <div className="relative h-full max-h-screen aspect-[9/16] mx-auto flex items-center justify-center">

                {/* PROGRESS BAR */}
                <div className="absolute top-4 left-4 right-4 flex gap-1">
                    {stories.map((_, i) => (
                        <div key={i} className="flex-1 h-1 bg-white/30 rounded">
                            <div
                                className="h-full bg-white rounded transition-all"
                                style={{
                                    width:
                                        i < currentIndex
                                            ? "100%"
                                            : i === currentIndex
                                                ? `${progress}%`
                                                : "0%",
                                }}
                            />
                        </div>
                    ))}
                </div>

                {/* HEADER */}
                <div className="absolute top-10 left-4 text-white z-50">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                    >
                        <MdClose size={26} />
                    </button>
                </div>

                <div className="absolute top-10 right-4 flex gap-4 text-white z-50">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setMuted(m => !m);
                        }}
                    >
                        {muted ? <MdVolumeOff size={24} /> : <MdVolumeUp size={24} />}
                    </button>

                    {showMenuOption && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowPopup(true);
                            }}
                            className="text-white text-3xl"
                        >
                            <MdMoreHoriz />
                        </button>
                    )}
                </div>

                {/* VIDEO */}
                <video
                    ref={videoRef}
                    src={getMediaUrl(currentStory.url)}
                    className="max-h-full max-w-full object-contain pointer-events-none"
                    muted={muted}
                    autoPlay
                    playsInline
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={handleEnded}
                />

                {/* BOTTOM NAVIGATION */}
                <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 z-50">
                    <div className="flex items-center gap-4 bg-black/60 px-6 py-3 rounded-full backdrop-blur-md">

                        {/* PREVIOUS */}
                        <button
                            onClick={handlePrevious}
                            disabled={currentIndex === 0}
                            className={`px-4 py-2 text-white rounded-full text-sm font-medium
                                    ${currentIndex === 0 ? "opacity-40 cursor-not-allowed" : "hover:bg-white/20"}`}
                        >
                            Previous
                        </button>

                        {/* DIVIDER */}
                        <div className="w-px h-5 bg-white/30" />

                        {/* NEXT */}
                        <button
                            onClick={handleNext}
                            className="px-4 py-2 text-white rounded-full text-sm font-medium hover:bg-white/20"
                        >
                            Next
                        </button>

                    </div>
                </div>

                {/* TAP ZONES */}
                <div className="md:hidden absolute inset-0 z-40 flex pointer-events-none">

                    {/* LEFT TAP */}
                    <div
                        className="w-[40%] h-full pointer-events-auto"
                        onClick={handlePrevious}
                    />

                    {/* CENTER */}
                    <div className="w-[20%] h-full" />

                    {/* RIGHT TAP */}
                    <div
                        className="w-[40%] h-full pointer-events-auto"
                        onClick={handleNext}
                    />
                </div>

                <DeleteStory
                    isOpen={showPopup}
                    onClose={() => setShowPopup(false)}
                    onDelete={() => OnDeleteStory(currentStory._id)}
                />
            </div>
        </div>
    );
}